import {
  PatientTreatmentPlan,
  PatientTreatmentPlanStageLog,
  StageType,
} from "../../../../../../types/healthcare/TreatmentPlanTypes";

/** Structural stage types that aren't counted as patient-facing plan stages. */
export const HIDDEN_PROGRESS_STAGE_TYPES: StageType[] = [
  "trigger",
  "watcher",
  "condition",
  "time_condition",
];

const isVisibleType = (t: StageType | undefined): boolean =>
  !!t && !HIDDEN_PROGRESS_STAGE_TYPES.includes(t);

/**
 * Plan progress measured against EVERY stage in the plan (all non-structural stages declared on the
 * canvas), not just the stages reached so far. Completed is the count of distinct visible stages
 * that have a completed log (capped at the total so loop re-entries can't overshoot). Falls back to
 * the stage logs when the template has no canvas data.
 */
export function computeProgress(
  plan: PatientTreatmentPlan,
  logs: PatientTreatmentPlanStageLog[],
): { completed: number; total: number } {
  const nodes = plan.template?.canvas_data?.nodes ?? [];
  const visibleNodes = nodes.filter(
    (n) => n.type !== "section_group" && isVisibleType(n.data?.stage_type as StageType),
  );

  const completedKeys = new Set(
    logs
      .filter(
        (l) =>
          l.status === "completed" &&
          isVisibleType((l.stage_snapshot?.stage_type ?? l.stage?.stage_type) as StageType),
      )
      .map((l) => l.stage_snapshot?.stage_key ?? l.stage?.stage_key)
      .filter(Boolean) as string[],
  );

  const total =
    visibleNodes.length > 0
      ? visibleNodes.length
      : logs.filter((l) => isVisibleType(l.stage_snapshot?.stage_type as StageType)).length;

  return { completed: Math.min(completedKeys.size, total || completedKeys.size), total };
}
